import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    type ReactNode,
} from "react";

const STORAGE_KEY = "webapp2.control.bboxOverlaySource";

export type BboxOverlaySource = "yolo26";

type BboxOverlaySourceContextValue = {
    bboxOverlaySource: BboxOverlaySource;
    setBboxOverlaySource: (v: BboxOverlaySource) => void;
};

const BboxOverlaySourceContext = createContext<BboxOverlaySourceContextValue | null>(
    null
);
BboxOverlaySourceContext.displayName = "BboxOverlaySourceContext";

function loadStored(): BboxOverlaySource {
    try {
        const r = localStorage.getItem(STORAGE_KEY);
        // NanoOWL is retired; migrate any stored preference to YOLO.
        if (r === "yolo26") return "yolo26";
    } catch {
        /* ignore */
    }
    return "yolo26";
}

export const BboxOverlaySourceProvider = ({ children }: { children: ReactNode }) => {
    const [bboxOverlaySource, setState] = useState<BboxOverlaySource>(loadStored);

    const setBboxOverlaySource = useCallback((v: BboxOverlaySource) => {
        // NanoOWL is retired; never persist that source.
        const next: BboxOverlaySource = v;
        setState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            /* ignore */
        }
    }, []);

    const value = useMemo(
        () => ({ bboxOverlaySource, setBboxOverlaySource }),
        [bboxOverlaySource, setBboxOverlaySource]
    );

    return (
        <BboxOverlaySourceContext.Provider value={value}>
            {children}
        </BboxOverlaySourceContext.Provider>
    );
};

/** Which HTTP service feeds front-camera bbox overlay (control page / StreamView). */
export function useBboxOverlaySource(): BboxOverlaySourceContextValue {
    const ctx = useContext(BboxOverlaySourceContext);
    if (!ctx) {
        return {
            bboxOverlaySource: "yolo26",
            setBboxOverlaySource: () => {
                /* no provider (e.g. tests): keep default */
            },
        };
    }
    return ctx;
}

export default BboxOverlaySourceProvider;
